import React, { useState, useEffect } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import { getBrandSupports, createBrandSupport, updateBrandSupport, deleteBrandSupport } from '../config/api';
import { toast } from 'react-toastify';

const BrandSupport = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    brand_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
  });

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const response = await getBrandSupports();
      if (response && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Error loading brand supports:', error);
      toast.error('Failed to load brand support details');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setFormData({ brand_name: '', email: '', phone: '', whatsapp_number: '' });
    setShowModal(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      brand_name: brand.brand_name || '',
      email: brand.email || '',
      phone: brand.phone || '',
      whatsapp_number: brand.whatsapp_number || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Are you sure you want to delete "${brand.brand_name}"?`)) return;
    try {
      await deleteBrandSupport(brand.id);
      toast.success('Brand deleted successfully');
      loadBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Failed to delete brand');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brand_name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingBrand) {
        await updateBrandSupport(editingBrand.id, formData);
        toast.success('Brand updated successfully');
      } else {
        await createBrandSupport(formData);
        toast.success('Brand created successfully');
      }
      setShowModal(false);
      loadBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error(editingBrand ? 'Failed to update brand' : 'Failed to create brand');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (brand) => {
    try {
      await updateBrandSupport(brand.id, { is_active: !brand.is_active });
      toast.success(`Brand ${brand.is_active ? 'deactivated' : 'activated'} successfully`);
      loadBrands();
    } catch (error) {
      console.error('Error toggling brand status:', error);
      toast.error('Failed to update brand status');
    }
  };

  const filteredBrands = brands.filter((brand) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      (brand.brand_name || '').toLowerCase().includes(search) ||
      (brand.email || '').toLowerCase().includes(search) ||
      (brand.phone || '').toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      name: 'Brand Name',
      selector: (row) => row.brand_name,
      sortable: true,
      cell: (row) => <span style={{ fontWeight: '600' }}>{row.brand_name}</span>,
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => row.email || <span style={{ color: '#999' }}>—</span>,
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
      cell: (row) => row.phone || <span style={{ color: '#999' }}>—</span>,
    },
    {
      name: 'WhatsApp',
      selector: (row) => row.whatsapp_number,
      sortable: true,
      cell: (row) => row.whatsapp_number || <span style={{ color: '#999' }}>—</span>,
    },
    {
      name: 'Status',
      selector: (row) => row.is_active,
      sortable: true,
      cell: (row) => (
        <span
          className={`status-badge ${row.is_active ? 'status-confirmed' : 'status-cancelled'}`}
          style={{ cursor: 'pointer' }}
          onClick={() => handleToggleActive(row)}
          title="Click to toggle"
        >
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-primary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
            onClick={() => handleEdit(row)}
          >
            Edit
          </button>
          <button
            className="btn"
            style={{ padding: '4px 12px', fontSize: '12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px' }}
            onClick={() => handleDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Brand Support</h1>
        <p className="page-subtitle">Manage brand contact and support information</p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search by brand name, email, or phone..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ maxWidth: '350px' }}
          />
          <button className="btn btn-primary" onClick={handleAdd}>
            + Add Brand
          </button>
        </div>

        <DataTable
          columns={columns}
          data={filteredBrands}
          progressPending={loading}
          pagination
          paginationPerPage={15}
          highlightOnHover
          noDataComponent="No brand support entries found"
        />
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Brand Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    placeholder="Enter brand name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className="form-control"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Number</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className="form-control"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowModal(false)} style={{ marginRight: '8px' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingBrand ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSupport;
